import { Link } from "wouter";
import { Menu, Play, X } from "lucide-react";
import { AnimatePresence, motion, useMotionValueEvent, useScroll } from "framer-motion";
import { useState } from "react";
import { GOOGLE_PLAY_URL, trackGooglePlayClick } from "@/lib/analytics";

const navigationItems = [
  { href: "/#how-it-works", label: "How it works" },
  { href: "/#capabilities", label: "Capabilities" },
  { href: "/#why-proteo", label: "Why Proteo" },
  { href: "/#pricing", label: "Proteo+" },
  { href: "/longevity/", label: "Longevity Hub" },
];

export function Navbar() {
  const { scrollY } = useScroll();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useMotionValueEvent(scrollY, "change", (latest) => {
    setScrolled(latest > 20);
  });

  return (
    <motion.header
      className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        scrolled ? "bg-background/80 backdrop-blur-2xl border-b border-border/50 py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="site-container flex items-center justify-between">
        <Link data-testid="link-brand-home" href="/" className="flex items-center gap-3 group">
          <div className="w-10 h-10 rounded-xl bg-primary text-primary-foreground flex items-center justify-center font-heading font-bold text-xl group-hover:scale-105 transition-transform shadow-lg shadow-primary/20">
            P
          </div>
          <span className="font-heading font-bold text-2xl tracking-tight">Proteo</span>
        </Link>
        
        <nav className="hidden lg:flex items-center gap-8 px-8 py-3 bg-foreground/5 rounded-full border border-foreground/5 backdrop-blur-md" aria-label="Primary navigation">
          {navigationItems.map((item) => (
            <a data-testid={`link-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`} key={item.href} href={item.href} className="text-sm font-bold text-foreground/70 hover:text-foreground transition-colors">
              {item.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a 
            href={GOOGLE_PLAY_URL}
            target="_blank"
            rel="noopener noreferrer"
             onClick={() => trackGooglePlayClick('navigation')}
             data-testid="link-google-play-nav"
             aria-label="Get Proteo on Google Play"
             className="flex min-h-11 shrink-0 items-center gap-2 whitespace-nowrap rounded-full border border-background/80 bg-foreground px-4 py-2.5 text-sm font-bold text-white shadow-[0_10px_24px_-12px_hsl(var(--foreground)/.8)] transition-all hover:-translate-y-0.5 hover:bg-foreground/90 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary active:scale-95 sm:px-5 sm:py-3"
          >
             <Play className="w-4 h-4 fill-current" />
             <span className="hidden sm:inline">Get it on Google Play</span>
              <span className="sm:hidden">Google Play</span>
          </a>
            <button data-testid="button-toggle-navigation"
            type="button"
              className="lg:hidden inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-foreground/10 bg-card text-foreground transition-colors hover:bg-secondary"
            aria-label={menuOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={menuOpen}
            aria-controls="mobile-navigation"
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
        <AnimatePresence>
          {menuOpen && (
            <motion.nav
              id="mobile-navigation"
              aria-label="Mobile navigation"
              initial={{ opacity: 0, y: -12, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -12, scale: 0.98 }}
              transition={{ duration: 0.18 }}
              className="absolute left-0 right-0 top-full mx-auto mt-3 w-[calc(100%-2.5rem)] rounded-3xl border border-border bg-card p-3 shadow-2xl shadow-foreground/10 lg:hidden"
            >
              <div className="grid gap-1">
                {navigationItems.map((item) => (
                  <a data-testid={`link-mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    key={item.href}
                    href={item.href}
                    onClick={() => setMenuOpen(false)}
                    className="rounded-2xl px-4 py-3 text-base font-bold text-foreground transition-colors hover:bg-secondary"
                  >
                    {item.label}
                  </a>
                ))}
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
}
